/**
 * A tab that outlived a deploy (ADJ-1016-8, owner note 23).
 *
 * Every deploy deletes the previous build's files from the bucket
 * (`.github/workflows/deploy-app.yml`), and every screen past the shell is a
 * lazy chunk. So a tab opened before the deploy asks for a chunk that is no
 * longer there the first time it opens a screen it had not opened yet — "Design
 * zone fails the first time, then works after a refresh". The refresh is the
 * fix: it fetches the new `index.html`, which names the new chunks. This does
 * that refresh once, by itself.
 *
 * **Once.** A chunk that is broken rather than stale would reload forever, so
 * the reload is remembered in `sessionStorage` for a few seconds and a second
 * failure in that window is left to the route's error screen. With no storage
 * there is no way to remember, and nothing is reloaded.
 */

const KEY = "km-chunk-reload";

/** How long one reload covers: a failure after it is a real one. */
export const RELOAD_WINDOW_MS = 10_000;

/** The browsers' words for a dynamic import whose file is gone. */
const CHUNK_FAILURE =
	/Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

/** Whether an error is a lazy chunk that did not load. */
export function isChunkLoadError(error: unknown): boolean {
	const message = error instanceof Error ? error.message : typeof error === "string" ? error : "";
	return CHUNK_FAILURE.test(message);
}

function session(): Storage | null {
	try {
		return window.sessionStorage;
	} catch {
		return null;
	}
}

/**
 * Reloads the page unless it already did within [RELOAD_WINDOW_MS]. Answers
 * whether it reloaded, so a caller can stand down instead of showing an error
 * for a page that is about to go away.
 */
export function reloadOnce(
	storage: Storage | null = session(),
	now: number = Date.now(),
	reload: () => void = () => window.location.reload(),
): boolean {
	if (storage === null) {
		return false;
	}
	try {
		const last = Number(storage.getItem(KEY) ?? "0");
		if (now - last < RELOAD_WINDOW_MS) {
			return false;
		}
		storage.setItem(KEY, String(now));
	} catch {
		return false;
	}
	reload();
	return true;
}

/**
 * Vite's own signal that a dynamic import failed: reload once, and keep the
 * error from reaching the screen while the page goes. Call once at startup.
 */
export function reloadOnStaleChunk(): void {
	window.addEventListener("vite:preloadError", (event) => {
		if (reloadOnce()) {
			event.preventDefault();
		}
	});
}
